import { RefinanceDebt } from "@/features/refinance/types";
import { toAmount, toAmountOrZero } from "@/shared/amount";
import { sumCommittedDebts } from "./debt-status";
import { resolveApprovedCashAmount } from "./resolve-cash-amount";

export interface RefinanceOfferParts {
	finalMaxAmount?: number | null;
	finalLoanAmount?: number | null;
	debtsToBeRedeemed?: number | null;
	approvedCashAmount?: number | null;
	/** A top-up's existing Lea Bank loan, which `debtsToBeRedeemed` includes. */
	existingLoanCapital?: number | null;
}

/** One priority band of the refinance capacity, and how much of it is covered. */
export interface RefinanceZone {
	capacity: number;
	filled: number;
	remaining: number;
}

export interface RefinanceAllocationInput {
	offer: RefinanceOfferParts | null | undefined;
	/** What the customer said they wanted to refinance when choosing purpose. */
	appliedRefinance: number;
	/** The customer's committed debts; the existing Lea Bank loan comes on top. */
	collected: number;
}

export interface RefinanceAllocation {
	cash: number;
	appliedRefinance: number;
	/** The loan before any upsell headroom is used. */
	baselineLoan: number;
	maxLoanAmount: number;
	/** The existing Lea Bank loan a top-up settles, covered from the start. */
	internal: number;
	/** The redemption the customer has to cover with debts of their own. */
	mandatory: RefinanceZone;
	voluntary: RefinanceZone;
	headroom: RefinanceZone;
	/** The cash payout, and how much of it the collected debt has converted. */
	payout: RefinanceZone;
	totalCapacity: number;
	/** The existing Lea Bank loan plus the committed debts. */
	collected: number;
	excess: number;
	loanAmount: number;
	cashPaidOut: number;
	/** The required redemption, until the collected debt passes it. */
	refinanced: number;
}

const fillZone = (capacity: number, available: number): RefinanceZone => {
	const filled = Math.min(Math.max(available, 0), capacity);

	return { capacity, filled, remaining: capacity - filled };
};

// The allocation runs in whole cents, so debts with cents add up exactly.
const toCentsOrNull = (value: unknown): number | null => {
	const amount = toAmount(value);

	return amount === null ? null : Math.round(amount * 100);
};

const toCents = (value: unknown): number => toCentsOrNull(value) ?? 0;

const fromCents = (cents: number): number => cents / 100;

const zoneFromCents = (zone: RefinanceZone): RefinanceZone => ({
	capacity: fromCents(zone.capacity),
	filled: fromCents(zone.filled),
	remaining: fromCents(zone.remaining),
});

/**
 * Splits the refinance capacity into mandatory, voluntary, upsell headroom and
 * finally the cash payout, then pours the collected debt into them in that
 * order. Where a debt came from is deliberately not an input: UC suggestions
 * and manual additions allocate identically. A top-up's existing Lea Bank loan
 * sits ahead of all four, already covered.
 */
export const resolveRefinanceAllocation = ({
	offer,
	appliedRefinance,
	collected,
}: RefinanceAllocationInput): RefinanceAllocation => {
	const applied = Math.max(toCents(appliedRefinance), 0);
	const redemption = Math.max(toCents(offer?.debtsToBeRedeemed), 0);
	const internal = Math.min(
		Math.max(toCents(offer?.existingLoanCapital), 0),
		redemption
	);
	// The applied refinance names the customer's own debts, never the existing
	// loan, so only the external requirement is measured against it.
	const mandatoryCapacity = redemption - internal;
	const appliedVoluntary = Math.max(applied - mandatoryCapacity, 0);
	const finalLoanAmount = toCentsOrNull(offer?.finalLoanAmount);
	const finalMaxAmount = toCentsOrNull(offer?.finalMaxAmount);

	// The loan is the approval, never above the ceiling. The applied refinance
	// only decides how much of it above the required redemption is voluntary.
	const offeredLoan =
		finalLoanAmount ??
		toCents(resolveApprovedCashAmount(offer, fromCents(applied))) +
			redemption +
			appliedVoluntary;
	const loanCeiling = finalMaxAmount ?? offeredLoan;
	const baselineLoan = Math.min(offeredLoan, loanCeiling);
	const voluntaryCapacity = Math.min(
		appliedVoluntary,
		Math.max(baselineLoan - redemption, 0)
	);
	const cash = Math.max(baselineLoan - redemption - voluntaryCapacity, 0);
	// Upsell headroom is offered in whole euros.
	const headroomCapacity = Math.max(
		Math.ceil((loanCeiling - baselineLoan) / 100) * 100,
		0
	);

	const collectedDebt = Math.max(toCents(collected), 0);

	const mandatory = fillZone(mandatoryCapacity, collectedDebt);
	const voluntary = fillZone(
		voluntaryCapacity,
		collectedDebt - mandatory.capacity
	);
	const headroom = fillZone(
		headroomCapacity,
		collectedDebt - mandatory.capacity - voluntary.capacity
	);
	// Once the loan cannot be raised any further, more debt converts the cash
	// payout instead of growing the loan. Allowed, but it costs the customer.
	const payout = fillZone(
		cash,
		collectedDebt - mandatory.capacity - voluntary.capacity - headroom.capacity
	);

	const totalCapacity =
		internal + mandatory.capacity + voluntary.capacity + headroom.capacity;
	const maxLoanAmount = baselineLoan + headroomCapacity;
	const loanAmount = baselineLoan + headroom.filled;
	// Only a required redemption is refinance the loan carries whether or not the
	// customer has covered it. Everything they are free to leave alone reaches
	// their account until a debt is collected into it.
	const refinanced = Math.min(
		internal + Math.max(mandatoryCapacity, collectedDebt),
		loanAmount
	);
	const cashPaidOut = loanAmount - refinanced;
	const redeemed = internal + collectedDebt;

	return {
		cash: fromCents(cash),
		appliedRefinance: fromCents(applied),
		baselineLoan: fromCents(baselineLoan),
		maxLoanAmount: fromCents(maxLoanAmount),
		internal: fromCents(internal),
		mandatory: zoneFromCents(mandatory),
		voluntary: zoneFromCents(voluntary),
		headroom: zoneFromCents(headroom),
		payout: zoneFromCents(payout),
		totalCapacity: fromCents(totalCapacity),
		collected: fromCents(redeemed),
		// Only debt the loan cannot pay for at all is an error. Overshooting
		// `totalCapacity` merely converts the payout, which `payout` reports.
		excess: fromCents(Math.max(redeemed - maxLoanAmount, 0)),
		loanAmount: fromCents(loanAmount),
		cashPaidOut: fromCents(cashPaidOut),
		refinanced: fromCents(refinanced),
	};
};

export type RefinanceDebtIssue = "belowMandatory" | "aboveCeiling";

export interface RefinanceDebtIssuesInput {
	offer: RefinanceOfferParts | null | undefined;
	appliedRefinance: number;
	debts: RefinanceDebt[];
}

export const findRefinanceDebtIssues = ({
	offer,
	appliedRefinance,
	debts,
}: RefinanceDebtIssuesInput): RefinanceDebtIssue[] => {
	const { mandatory, excess } = resolveRefinanceAllocation({
		offer,
		appliedRefinance,
		collected: sumCommittedDebts(debts),
	});

	const issues: RefinanceDebtIssue[] = [];

	if (mandatory.remaining > 0) {
		issues.push("belowMandatory");
	}

	if (excess > 0) {
		issues.push("aboveCeiling");
	}

	return issues;
};

/**
 * True when the step has nothing to offer: nothing required, nothing applied
 * for, and nothing found by the credit check. FLOW should not route the task at
 * all in that case, so this only guards against it doing so anyway.
 */
export const hasNothingToRefinance = ({
	offer,
	appliedRefinance,
	ucDebtCount,
}: {
	offer: RefinanceOfferParts | null | undefined;
	appliedRefinance: number;
	ucDebtCount: number;
}): boolean =>
	toAmountOrZero(appliedRefinance) <= 0 &&
	toAmountOrZero(offer?.debtsToBeRedeemed) <= 0 &&
	ucDebtCount === 0;
